import { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { ApiError } from '../api/client.ts';
import type { PlanLock, PlanRole, PlanSummary, Tier } from '../api/types.ts';
import { t } from '../i18n/index.ts';
import { LEGACY_PLAN_LIMIT, ownerName, useActivePlan, usePlanLimit, useStore } from '../state/store.tsx';
import { useToast } from '../state/toast.tsx';
import { Icon, type IconName } from './Icon.tsx';
import { tierName, upgradePath, useUpgradePrompt } from './UpgradePrompt.tsx';
import { canUpgrade } from '../lib/billing.ts';

export const MAX_PLANS = LEGACY_PLAN_LIMIT;

export const atPlanLimit = (owned: number, limit: number | null) => limit !== null && owned >= limit;

/** Plans counted against the cap as the server counts them: owned plans, plus the slot it keeps for a primary plan not created yet. */
export const plansCounted = (plans: readonly PlanSummary[]): number => {
  const own = plans.filter((p) => p.role === 'owner');
  return own.length + (own.length > 0 && !own.some((p) => p.isPrimary) ? 1 : 0);
};

export type PlanKind = 'primary' | 'copy' | 'yours' | 'shared';
export const planKind = (p: PlanSummary): PlanKind =>
  p.role !== 'owner' ? 'shared' : p.isPrimary ? 'primary' : p.parentPlanId ? 'copy' : 'yours';

const KIND_ICON: Record<PlanKind, IconName> = { primary: 'pin', copy: 'branch', yours: 'plans', shared: 'people' };
const ROLE_ICON: Record<PlanRole, IconName> = { owner: 'key', editor: 'pen', viewer: 'eye' };

export function PlanMark({ plan, size = 40 }: { plan: PlanSummary; size?: number }) {
  const kind = planKind(plan);
  return (
    <span className={`plan-mark plan-mark--${kind}`} style={{ width: size, height: size }} aria-hidden="true">
      <Icon name={KIND_ICON[kind]} size={Math.round(size * 0.46)} />
    </span>
  );
}

export function RoleBadge({ role }: { role: PlanRole }) {
  return (
    <span className={`role-badge role-badge--${role}`}>
      <Icon name={ROLE_ICON[role]} size={12} strokeWidth={2.2} />
      {t(`plans.role.${role}`)}
    </span>
  );
}

export const kindLabel = (p: PlanSummary) =>
  t(p.role !== 'owner' ? 'plans.kind.shared' : p.isPrimary ? 'plans.kind.primary' : 'plans.kind.yours');

/** Own plans with copies under their parent, then plans shared with the caller. */
export function orderPlans(plans: readonly PlanSummary[]): { own: { plan: PlanSummary; child: boolean }[]; shared: PlanSummary[] } {
  const mine = plans.filter((p) => p.role === 'owner');
  const isChild = (p: PlanSummary) => !!p.parentPlanId && mine.some((q) => q.id === p.parentPlanId);
  const own = mine.filter((p) => !isChild(p)).flatMap((root) => [
    { plan: root, child: false },
    ...mine.filter((c) => c.parentPlanId === root.id).map((plan) => ({ plan, child: true })),
  ]);
  return { own, shared: plans.filter((p) => p.role !== 'owner') };
}

function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      ref.current?.querySelector<HTMLElement>('.switcher__button')?.focus();
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    (ref.current?.querySelector<HTMLElement>('.switcher__item[aria-current="true"]') ?? ref.current?.querySelector<HTMLElement>('.switcher__item'))?.focus();
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', esc);
    };
  }, [open]);
  return { open, setOpen, ref };
}

/** Opens a plan and lands on its Home. */
export function useOpenPlan() {
  const { switchPlan, activePlanId } = useStore();
  const navigate = useNavigate();
  return (plan: PlanSummary) => {
    navigate('/');
    if (plan.id !== activePlanId) void switchPlan(plan.id);
  };
}

/** Desktop: in the sidebar. Mobile: the compact bar above each screen, for people with more than one plan. */
export function PlanSwitcher({ variant }: { variant: 'sidebar' | 'bar' }) {
  const { plans, billing } = useStore();
  const { plan: active, multiple } = useActivePlan();
  const limit = usePlanLimit();
  const { open, setOpen, ref } = usePopover();
  const openPlan = useOpenPlan();
  const menuId = useId();
  if (!active) return null;
  const { own, shared } = orderPlans(plans);
  const owned = plansCounted(plans);
  const quiet = !multiple;
  const pick = (plan: PlanSummary) => {
    setOpen(false);
    openPlan(plan);
  };
  const item = (plan: PlanSummary, child = false) => (
    <li key={plan.id}>
      <button
        type="button"
        className={`switcher__item ${child ? 'switcher__item--child' : ''}`}
        aria-current={plan.id === active.id ? 'true' : undefined}
        onClick={() => pick(plan)}
      >
        <PlanMark plan={plan} size={28} />
        <span className="switcher__item-name">{plan.name}</span>
        {plan.role !== 'owner' && <RoleBadge role={plan.role} />}
        {plan.id === active.id && <span className="switcher__check"><Icon name="check" size={16} strokeWidth={2.4} /></span>}
      </button>
    </li>
  );
  return (
    <div className={`switcher switcher--${variant} ${quiet ? 'switcher--quiet' : ''}`} ref={ref}>
      <button
        type="button"
        className="switcher__button"
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={t('plans.switcher.label', { name: active.name })}
        onClick={() => setOpen((o) => !o)}
      >
        {quiet ? <Icon name="pin" size={15} className="switcher__pin" /> : <PlanMark plan={active} size={variant === 'bar' ? 30 : 32} />}
        <span className="switcher__text">
          {!quiet && variant === 'sidebar' && <span className="switcher__eyebrow">{kindLabel(active)}</span>}
          <span className="switcher__name">{active.name}</span>
          {!quiet && variant === 'sidebar' && active.role !== 'owner' && <span className="switcher__role"><RoleBadge role={active.role} /></span>}
        </span>
        {!quiet && variant === 'bar' && <RoleBadge role={active.role} />}
        <Icon name="chevronDown" size={15} className="switcher__chevron" />
      </button>
      {open && (
        <div className="switcher__menu" id={menuId} role="region" aria-label={t('plans.switcher.menu')}>
          <p className="switcher__group">{t('plans.yours')}</p>
          <ul>{own.map(({ plan, child }) => item(plan, child))}</ul>
          {shared.length > 0 && (
            <>
              <p className="switcher__group">{t('plans.sharedWithYou')}</p>
              <ul>{shared.map((p) => item(p))}</ul>
            </>
          )}
          <div className="switcher__foot">
            {!atPlanLimit(owned, limit) ? (
              <Link className="switcher__item switcher__item--action" to="/plans?new=1" onClick={() => setOpen(false)}>
                <span className="switcher__action-icon" aria-hidden="true"><Icon name="plus" size={15} strokeWidth={2.4} /></span>
                <span className="switcher__item-name">{t('plans.new')}</span>
              </Link>
            ) : canUpgrade(billing) && (
              <Link className="switcher__item switcher__item--action" to={upgradePath(billing?.lapsed ? 'lapsed' : 'plans')} onClick={() => setOpen(false)}>
                <span className="switcher__action-icon switcher__action-icon--upgrade" aria-hidden="true"><Icon name="sparkle" size={15} /></span>
                <span className="switcher__item-name">{billing?.lapsed ? t('billing.resubscribe') : t('plans.switcher.more')}</span>
              </Link>
            )}
            <Link className="switcher__item switcher__item--action" to="/plans" onClick={() => setOpen(false)}>
              <span className="switcher__action-icon" aria-hidden="true"><Icon name="plans" size={15} /></span>
              <span className="switcher__item-name">{t('plans.switcher.manage')}</span>
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

/** A viewer can look but not change: says so, and offers a copy they own. A plan billing has paused says why, calmly. */
export function ReadOnlyRibbon() {
  const { pathname } = useLocation();
  const { plan, readOnlyReason } = useActivePlan();
  const { createPlan, switchPlan, plans, billing } = useStore();
  const limit = usePlanLimit();
  const promptUpgrade = useUpgradePrompt();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  if (plan?.locked === 'lapsed' && pathname === '/upgrade') return null;
  if (plan?.locked) return <LockedBanner lock={plan.locked} tier={billing?.tier ?? 'none'} owner={plan.role === 'owner'} />;
  if (!plan || !readOnlyReason) return null;
  const full = atPlanLimit(plansCounted(plans), limit);
  const copy = async () => {
    setBusy(true);
    try {
      const name = t('plans.new.copyName', { name: plan.name }).slice(0, 60);
      const created = await createPlan({ name, copyFrom: plan.id });
      await switchPlan(created.id);
      navigate('/');
      toast.push({ tone: 'success', message: t('plans.readOnly.copied', { name: created.name }) });
    } catch (error) {
      const upgrade = error instanceof ApiError ? error.upgrade : null;
      if (upgrade) promptUpgrade(upgrade);
      else toast.push({ tone: 'error', message: planErrorMessage(error) });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="ribbon" role="note">
      <Icon name="eye" size={16} />
      <span className="ribbon__text">{readOnlyReason}</span>
      {!full && (
        <button type="button" className="link-button ribbon__action" disabled={busy} onClick={() => void copy()}>
          {t('plans.readOnly.copy')}
        </button>
      )}
    </div>
  );
}

function LockedBanner({ lock, tier, owner }: { lock: PlanLock; tier: Tier; owner: boolean }) {
  const seat = lock === 'editor_seat';
  const text = seat
    ? t('plans.locked.editorSeat')
    : lock === 'lapsed'
      ? t(owner ? 'plans.locked.lapsed' : 'plans.locked.lapsedMember')
      : t('plans.locked.planLimit', { tier: tierName(tier) });
  const cta = lock === 'lapsed' ? (owner ? { feature: 'lapsed' as const, label: t('billing.resubscribe') } : null)
    : seat ? null : { feature: 'plan_read_only' as const, label: t('billing.upgrade') };
  return (
    <section className="locked-banner" role="note" aria-label={t('plans.locked')}>
      <span className="locked-banner__icon" aria-hidden="true"><Icon name={seat ? 'pen' : 'lock'} size={17} /></span>
      <p className="locked-banner__text">{text}</p>
      {cta && (
        <Link to={upgradePath(cta.feature)} className="button button--sm button--outline-mint locked-banner__cta">
          {cta.label}<Icon name="arrowRight" size={14} />
        </Link>
      )}
    </section>
  );
}

export function planErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isPlanTooLarge) return error.message;
    if (error.code === 'plan_limit') return t('plans.error.limit', { n: error.body?.limit ?? MAX_PLANS });
    if (error.status === 404) return t('plans.error.notFound');
    if (error.status === 403) return t('plans.error.forbidden');
  }
  return t('plans.error.generic');
}

export const ownerLabel = (plan: PlanSummary) => ownerName(plan) ?? plan.ownerEmail ?? '';
